import { useTenantContext } from "@/contexts/TenantContext"
import { supabase } from "@/lib/supabase/client"
import { isMockMode } from "@/lib/mock/mock-supabase"
import { CODIGO_MUITAS_TENTATIVAS } from "@/lib/limiteDeUso"

/**
 * Convenience hook for authentication state and actions.
 * Must be used within a TenantProvider.
 * In mock mode, signIn always succeeds and signOut clears state via context.
 */
export function useAuth() {
  const ctx = useTenantContext()

  const isAuthenticated = !!ctx.user

  const signIn = async (email: string, password: string) => {
    // ── Mock mode: signIn sempre bem-sucedido ────────────────────────────
    if (isMockMode) {
      // O TenantContext já inicializa com MOCK_USER automaticamente;
      // apenas ignoramos credenciais no mock mode.
      void email
      void password
      return
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    // Lança o erro original: a tela traduz por `describeAuthError` e registra o cru no console.
    if (error) throw error
  }

  const sendPasswordReset = async (email: string) => {
    if (isMockMode) {
      void email
      return
    }
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({ email }),
      },
    )
    // Muitos pedidos seguidos: a tela troca o código pelo texto de esperar.
    if (res.status === 429) throw new Error(CODIGO_MUITAS_TENTATIVAS)
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || "Erro ao enviar email de recuperação")
    }
  }

  // canAccess wraps isAtLeast for backward compatibility
  const canAccess = ctx.isAtLeast

  return {
    user: ctx.user,
    profile: ctx.profile,
    role: ctx.role,
    isAuthenticated,
    isLoading: ctx.isLoading,
    signIn,
    signOut: ctx.signOut,
    sendPasswordReset,
    hasRole: ctx.hasRole,
    canAccess,
    can: ctx.can,
  }
}
